<template>
  <section :class="['navbar', { 'is-contact': isContact, 'is-menu-open': isMenuOpen, 'is-banner': isBanner, 'is-image-left': isImageLeft }]">
    <div class="navbar-left">
      <RouterLink to="/" class="logo logo--split">
        <span class="logo-k gradient">K</span>
        <span class="logo-rest">STOKLOSA</span>
      </RouterLink>
    </div>
    <div class="navbar-right">
      <button
        class="navbar-toggle"
        type="button"
        :aria-expanded="isMenuOpen"
        aria-label="Toggle navigation menu"
        @click="toggleMenu"
      >
        <span :class="{ open: isMenuOpen }">
          <i></i>
          <i></i>
          <i></i>
        </span>
      </button>

      <nav :class="['navbar-menu', { 'navbar-menu--open': isMenuOpen }]">
        <ul>
          <li v-for="(link, index) in links" :key="index" @click="closeMenu">
            <RouterLink :to="`/${link}`">
              <span class="link-text" :data-text="link">{{ link }}</span>
            </RouterLink>
          </li>
        </ul>
      </nav>
    </div>

    <div
      v-if="isMenuOpen"
      class="navbar-overlay"
      @click="closeMenu"
    />
  </section>
</template>

<script setup>
  import { onMounted, onBeforeUnmount, ref, watch, nextTick } from 'vue';
  import { useRoute } from 'vue-router'

  const route = useRoute()

  const isContact = ref(false)
  const isMenuOpen = ref(false)
  const isBanner = ref(false)
  const isImageLeft = ref(false)

  const links = ['about', 'portfolio', 'contact']
  let observer = null
  let imagePositionInterval = null

  const syncRouteState = () => {
    isContact.value = route.name === 'contact'
    const isHomePage = route.name === 'home' || route.name === 'homeAlt'
    
    if (isHomePage) {
      setupObserver()
      startImagePositionCheck()
    } else {
      isBanner.value = false
      isImageLeft.value = false
      if (observer) {
        observer.disconnect()
        observer = null
      }
      stopImagePositionCheck()
    }
  }

  const checkImagePosition = () => {
    // Only check on desktop
    if (window.innerWidth <= 768) {
      isImageLeft.value = false
      return
    }

    // Check if banner__left (image on right) is visible
    const bannerLeft = document.querySelector('.banner__left')
    if (bannerLeft && window.getComputedStyle(bannerLeft).display !== 'none') {
      isImageLeft.value = false // Image is on the RIGHT in banner__left
    } else {
      isImageLeft.value = true // Image is on the LEFT in banner__right
    }
  }

  const startImagePositionCheck = () => {
    checkImagePosition()
    // Check every 100ms for slide changes
    imagePositionInterval = setInterval(checkImagePosition, 100)
  }

  const stopImagePositionCheck = () => {
    if (imagePositionInterval) {
      clearInterval(imagePositionInterval)
      imagePositionInterval = null
    }
  }

  const setupObserver = async () => {
    // Wait for DOM to be ready
    await nextTick()
    
    // Disconnect previous observer if exists
    if (observer) {
      observer.disconnect()
    }

    // Find the banner section
    const bannerSection = document.querySelector('.banner') || document.querySelector('.home-page')
    
    if (!bannerSection) {
      console.log('Banner section not found')
      return
    }

    // Create intersection observer
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Update isBanner based on intersection
          isBanner.value = entry.isIntersecting && entry.intersectionRatio > 0.3
        })
      },
      {
        threshold: [0, 0.1, 0.3, 0.5, 0.7, 1.0],
        rootMargin: '-10% 0px -10% 0px'
      }
    )

    observer.observe(bannerSection)
  }

  const closeMenu = () => {
    isMenuOpen.value = false
  }

  const toggleMenu = () => {
    isMenuOpen.value = !isMenuOpen.value
  }

  onMounted(() => {
    syncRouteState()
    window.addEventListener('resize', checkImagePosition)
  })

  onBeforeUnmount(() => {
    if (observer) {
      observer.disconnect()
    }
    stopImagePositionCheck()
    window.removeEventListener('resize', checkImagePosition)
  })

  watch(
    () => route.name,
    () => {
      syncRouteState()
      closeMenu()
    }
  )
</script>

<style lang="scss">
.logo--split {
  display: flex;
  align-items: center;
  gap: 8px;
  
  .gradient {
    background: linear-gradient(135deg, #fff 0%, rgba(255,255,255, 0.8) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    transition: all 0.4s ease;
    position: relative;
    filter: drop-shadow(0 0 20px rgba(255, 255, 255, 0.3));
    margin-right: 10px;
  }
  
  .logo-separator {
    font-size: 28px;
    font-weight: 200;
    opacity: 0.5;
    transition: all 0.4s ease;
    transform: rotate(20deg);
    position: relative;
    top: -2px;
  }
  
  .logo-rest {
    font-size: 32px;
    font-weight: 300;
    letter-spacing: 0.05em;
    transition: all 0.4s ease;
    opacity: 0.95;
  }
  
  &:hover {
    .gradient {
      filter: drop-shadow(0 0 30px rgba(255, 255, 255, 0.6));
      transform: scale(1.05);
    }
    
    .logo-separator {
      opacity: 1;
      transform: rotate(20deg) scale(1.1);
    }
    
    .logo-rest {
      letter-spacing: 0.2em;
      opacity: 1;
    }
  }
}

.navbar {
  font-family: "Courier New";
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  padding: 30px 25px;
  z-index: 10000;
  pointer-events: none;

  &-left,
  &-right {
    flex: 1;
    display: flex;
    align-items: center;
  }

  &-right {
    justify-content: flex-end;
    gap: 16px;
  }

  ul {
    list-style: none;
    display: flex;
    gap: 20px;
    margin: 0;
    padding: 0;
    justify-content: flex-end;
    padding-right: 30px;
  }

  a {
    color: white;
    font-size: 14px;
    text-decoration: none;
    pointer-events: auto;
    text-transform: uppercase;
    position: relative;
    display: inline-block;
    overflow: hidden;
    padding-bottom: 2px;

    .link-text {
      display: inline-block;
      position: relative;
      transition: all 0.3s ease;
    }

    &:hover {
      letter-spacing: 0.2em;
    }

    &.router-link-active {
      .link-text {
        font-weight: 500;
        letter-spacing: 0.15em;
        
        &::after {
          width: 100%;
          background: linear-gradient(90deg, 
            rgba(255, 255, 255, 0.3) 0%, 
            rgba(255, 255, 255, 1) 50%, 
            rgba(255, 255, 255, 0.3) 100%
          );
          height: 2px;
          box-shadow: 0 0 8px rgba(255, 255, 255, 0.5);
        }

        &::before {
          opacity: 0.15;
          filter: blur(2px);
        }
      }
    }
  }

  @keyframes animate-background {
    0% {
      background-position: 0 50%;
    }
    100% {
      background-position: 100% 50%;
    }
  }

  li {
    padding: 0 35px;
  }

  .logo {
    font-weight: 800;
    font-size: 22px;
    font-family: "Alumni Sans Pinstripe", sans-serif;
    color: white;
    pointer-events: auto;
    cursor: pointer;
    padding-left: 60px;
    position: relative;
    top: -10px;
    transition: color 0.3s ease;
  }

  .logo-k {
    font-size: 40px;
    position: relative;
    left: 3px;
    font-weight: 900;
  }

  .logo-rest {
    font-size: 25px;
  }

  &-toggle {
    display: none;
    pointer-events: auto;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 6px;

    span {
      display: inline-flex;
      flex-direction: column;
      gap: 6px;

      i {
        display: block;
        width: 28px;
        height: 2px;
        background: white;
        transition: transform .2s ease, opacity .2s ease, background .2s ease;
      }

      &.open {
        i:nth-child(1) {
          transform: translateY(8px) rotate(45deg);
        }

        i:nth-child(2) {
          opacity: 0;
        }

        i:nth-child(3) {
          transform: translateY(-8px) rotate(-45deg);
        }
      }
    }
  }

  &-menu {
    width: 100%;
  }

  &-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: -1;
  }

  // Desktop: When image is on the left (banner__right visible) - logo should be black
  &.is-banner.is-image-left {
    .logo {
      color: black;

      .logo-k,
      .logo-rest {
        color: inherit;
      }
    }

    .logo--split .gradient {
      background: linear-gradient(135deg, #000 0%, rgba(0,0,0, 0.8) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      filter: drop-shadow(0 0 20px rgba(0, 0, 0, 0.2));
    }
    
    .logo-separator {
      color: black;
    }
    
    .logo-rest {
      color: black;
    }
  }

@media (max-width: 1024px) {
  left: 0;
  padding: 20px;
  gap: 10px;

  .logo {
    padding-left: 0;
  }

  ul {
    gap: 15px;
    padding-right: 0;
  }
}

@media (max-width: 768px) {
  flex-direction: row;
  align-items: center;
  padding: 15px 20px;
  gap: 10px;
  pointer-events: auto;
  min-height: 72px;

    &-left {
      width: auto;
      flex: 0 0 auto;
    }

    &-right {
      width: auto;
      flex: 1;
      justify-content: flex-end;
    }

    &-toggle {
      display: inline-flex;
      margin-left: auto;
    }

    &-menu {
      width: 100vw;
      height: 100vh;
      position: fixed;
      inset: 0;
      background: rgba(255, 255, 255, 0.95);
      color: #050505;
      padding: 120px 30px 30px;
      transform: translateY(-10px);
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      transition: opacity .2s ease, transform .2s ease, visibility .2s;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;

      ul {
        flex-direction: column;
        gap: 20px;
        align-items: center;
        width: 100%;
      }
      
      a {
        font-size: 22px;
        letter-spacing: 0.12em;
        position: relative;
        text-align: center;
        color: #050505;
        overflow: visible;

        &:after {
          content: '';
          height: 1px;
          width: 60%;
          background-color: currentColor;
          position: absolute;
          left: 20%;
          bottom: -5px;
          transition: width 0.3s ease;
        }

        &:hover::after {
          width: 80%;
          left: 10%;
        }

        &.router-link-active {
          font-weight: 600;
          letter-spacing: 0.18em;
          
          .link-text::after {
            width: 80%;
            left: 10%;
            height: 2px;
            background: linear-gradient(90deg, 
              rgba(5, 5, 5, 0.3) 0%, 
              rgba(5, 5, 5, 1) 50%, 
              rgba(5, 5, 5, 0.3) 100%
            );
            box-shadow: 0 0 10px rgba(5, 5, 5, 0.3);
          }

          .link-text::before {
            opacity: 0.1;
            filter: blur(3px);
          }

          &::after {
            width: 80%;
            left: 10%;
            height: 2px;
            background: linear-gradient(90deg, 
              rgba(5, 5, 5, 0.3) 0%, 
              rgba(5, 5, 5, 1) 50%, 
              rgba(5, 5, 5, 0.3) 100%
            );
          }
        }
      }
      
      li {
        padding: 10px 0;
        width: 100%;
        display: flex;
        justify-content: center;
      }
    }

    &-menu--open {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
      transform: translateY(0);
    }

    &-overlay {
      z-index: 9998;
    }

    // Mobile: Override desktop image position logic
    &.is-image-left {
      .logo {
        color: white;
      }

      .logo--split .gradient {
        background: white;
      }
    }

    // Default mobile state (NOT banner) - white logo and hamburger
    .logo {
      top: 0;
      color: white;

      .logo-k,
      .logo-rest {
        color: inherit;
      }
    }

    .logo--split .gradient {
      background: linear-gradient(135deg, #fff 0%, rgba(255,255,255, 0.8) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      filter: drop-shadow(0 0 20px rgba(255, 255, 255, 0.2));
    }
    
    .logo-separator {
      color: white;
    }
    
    .logo-rest {
      color: white;
    }

    .navbar-toggle span i {
      background: white;
    }

    // When banner is visible (is-banner class) - black logo and hamburger
    &.is-banner {
      .logo {
        color: black;

        .logo-k,
        .logo-rest {
          color: inherit;
        }
      }

      .logo--split .gradient {
        background: linear-gradient(135deg, #000 0%, rgba(0,0,0, 0.8) 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        filter: drop-shadow(0 0 20px rgba(0, 0, 0, 0.2));
      }
      
      .logo-separator {
        color: black;
      }
      
      .logo-rest {
        color: black;
      }

      .navbar-toggle span i {
        background: black;
      }
    }

    // When menu is open
    &.is-menu-open {
      .navbar-left,
      .navbar-toggle {
        position: relative;
        z-index: 10001;
      }

      .navbar-right {
        position: relative;
        z-index: 10000;
      }

      .logo {
        color: black;

        .logo-k,
        .logo-rest {
          color: inherit;
        }
      }

      .logo--split .gradient {
        background: black;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      .navbar-toggle span i {
        background: black;
      }
    }
}

@media (max-width: 480px) {
  .logo {
    font-size: 18px;
  }

  .logo-k {
    font-size: 32px;
  }

  .logo-rest {
    font-size: 20px;
  }

  a {
    font-size: 20px;
  }
}
}
</style>